import {
  configuredString,
  type KioskPaymentRuntimeConfig,
} from '../config/kioskRuntimeConfig';
import type {
  EntrPaymentTransport,
  PayCardInput,
  PayCardOutput,
  TransportResult,
} from '../types';
import {
  createTerminalPaymentModule,
  type TerminalPaymentDeps,
  type TerminalPaymentOptions,
} from './terminalPayment';

export interface KioskPaymentRuntime extends EntrPaymentTransport {
  initialize(config: KioskPaymentRuntimeConfig): void;
}

export type KioskPaymentRuntimeDeps = Omit<
  TerminalPaymentDeps,
  'resolveReaderId' | 'resolvePaymentAccount'
>;

const DEFAULT_PAYMENT_CONFIG: KioskPaymentRuntimeConfig = {
  terminalPaymentEnabled: false,
  readerId: null,
  paymentDestination: 'platform',
  stripeAccountId: null,
};

export function createKioskPaymentRuntime(
  deps: KioskPaymentRuntimeDeps,
  options: TerminalPaymentOptions = {}
): KioskPaymentRuntime {
  let config = DEFAULT_PAYMENT_CONFIG;
  const terminal = createTerminalPaymentModule(
    {
      ...deps,
      resolveReaderId: () =>
        config.terminalPaymentEnabled
          ? configuredString(config.readerId)
          : null,
      resolvePaymentAccount: () => ({
        paymentDestination: config.paymentDestination,
        stripeAccountId: config.stripeAccountId,
        partnerStripeId:
          config.paymentDestination === 'partner'
            ? configuredString(config.stripeAccountId)
            : null,
      }),
    },
    options
  );

  return {
    initialize(nextConfig) {
      config = normalizePaymentConfig(nextConfig);
      assertPaymentConfig(config);
      deps.observer?.recordEvent(
        'kiosk.payment',
        'payment runtime initialized',
        {
          terminal_payment_enabled: config.terminalPaymentEnabled,
          reader_id: config.readerId,
          payment_destination: config.paymentDestination ?? null,
          has_stripe_account: Boolean(config.stripeAccountId),
        }
      );
    },

    async payCard(
      input: PayCardInput
    ): Promise<TransportResult<PayCardOutput>> {
      return terminal.payCard(input);
    },

    async cancelActivePayment(): Promise<void> {
      await terminal.cancelActivePayment();
    },
  };
}

function normalizePaymentConfig(
  config: KioskPaymentRuntimeConfig
): KioskPaymentRuntimeConfig {
  return {
    terminalPaymentEnabled: config.terminalPaymentEnabled,
    readerId: configuredString(config.readerId),
    paymentDestination: normalizePaymentDestination(config.paymentDestination),
    stripeAccountId: configuredString(config.stripeAccountId),
  };
}

function assertPaymentConfig(config: KioskPaymentRuntimeConfig): void {
  if (!config.terminalPaymentEnabled) return;
  if (!config.readerId) {
    throw new Error('terminal payments require a Stripe reader id');
  }
  if (config.paymentDestination !== 'partner') return;
  if (config.stripeAccountId) return;
  throw new Error('partner terminal payments require a Stripe account id');
}

function normalizePaymentDestination(
  value: string | null | undefined
): 'platform' | 'partner' {
  return value?.trim().toLowerCase() === 'partner' ? 'partner' : 'platform';
}
